import { isNumber } from "lodash";
import { useCallback, useState } from "react";
import ReactFocusLock from "react-focus-lock";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { Button, TextInput } from "@/shared/ui";
import {
  nextStep,
  personalDataErrorsSelector,
  personalDataSelector,
  personalDataSeparatedErrorsSelector,
  setPersonalData,
} from "@/domains/createCharacter/slice";
import toast from "@/domains/toasts/helpers/toast";
import usePersonalData from "@/domains/createCharacter/hooks/usePersonalData";
import CreateCharacterNameRulesModal from "@/domains/createCharacter/containers/modals/CreateCharacterNameRulesModal";
import CreateCharacterBtnRandom from "@/domains/createCharacter/components/CreateCharacterBtnRandom";
import CreateCharacterGender from "@/domains/createCharacter/components/CreateCharacterGender";
import CreateCharacterScrollArea from "@/domains/createCharacter/components/CreateCharacterScrollArea";
import CreateCharacterStepHeader from "@/domains/createCharacter/components/CreateCharacterStepHeader";

const regex = /[^а-яА-ЯёЁ]/g;

export default function CreateCharacterDataContainer() {
  const dispatch = useAppDispatch();
  const personalData = useAppSelector(personalDataSelector);
  const personalDataErrors = useAppSelector(personalDataErrorsSelector);
  const personalDataSeparatedErrors = useAppSelector(personalDataSeparatedErrorsSelector);
  const [isOpenModal, setIsOpenModal] = useState(false);

  const closeModal = useCallback(() => {
    setIsOpenModal(false);
  }, []);

  const openModal = useCallback(() => {
    setIsOpenModal(true);
  }, []);

  const changeFirstName = useCallback((value: string) => {
    dispatch(setPersonalData({ firstName: value.replace(regex, "") }));
  }, []);

  const changeLastName = useCallback((value: string) => {
    dispatch(setPersonalData({ lastName: value.replace(regex, "") }));
  }, []);

  const changeAge = useCallback((value: string) => {
    dispatch(setPersonalData({ age: Number(value) }));
  }, []);

  const { randomizeFirstName, randomizeLastName, randomizeAge, randomizeAll, onChangeGender } =
    usePersonalData();

  const continueHandler = useCallback(() => {
    if (personalDataErrors) {
      toast.game.warning(
        `Некорректный ${personalDataErrors[0].path} (${personalDataErrors[0].message})`,
      );
    } else {
      dispatch(nextStep());
    }
  }, [personalDataErrors]);

  return (
    <>
      <CreateCharacterNameRulesModal open={isOpenModal} onClose={closeModal} />
      <CreateCharacterStepHeader title="Данные" onRandom={randomizeAll} />
      <CreateCharacterScrollArea>
        <CreateCharacterGender value={personalData.gender} onChange={onChangeGender} />
        <div className="p-5 rounded-[0.75rem] bg-white/10 border border-white/15">
          <div className="text-16 font-bold text-inverted">Выбор имени и фамилии персонажа</div>
          <div className="text-15 font-medium text-white/50 mt-1.5">
            Придумайте уникальное Имя и Фамилию для вашего персонажа. Используйте только буквы
            русского алфавита. Чтобы более подробно узнать ограничения на используемые имена,
            разрешённые правилами сервера прочитайте информационный блок по ссылке ниже
          </div>
          <Button variant="soft" color="gray" className="mt-3.5 h-[2.75rem]" onClick={openModal}>
            ПРАВИЛА ИМЕН ПЕРСОНАЖЕЙ
          </Button>
        </div>
        <ReactFocusLock>
          <TextInput
            value={personalData.firstName}
            onChange={changeFirstName}
            required
            title="Имя:"
            placeholder="Введите имя"
            // eslint-disable-next-line jsx-a11y/tabindex-no-positive
            tabIndex={1}
            className="mt-3.5"
          >
            <CreateCharacterBtnRandom onClick={randomizeFirstName} />
          </TextInput>
          <TextInput
            value={personalData.lastName}
            onChange={changeLastName}
            required
            title="Фамилия:"
            placeholder="Введите фамилию"
            // eslint-disable-next-line jsx-a11y/tabindex-no-positive
            tabIndex={2}
            className="mt-3.5"
          >
            <CreateCharacterBtnRandom onClick={randomizeLastName} />
          </TextInput>

          <TextInput
            type="number"
            title="Возраст"
            value={String(personalData.age)}
            onChange={changeAge}
            required
            placeholder="Введите возраст"
            // eslint-disable-next-line jsx-a11y/tabindex-no-positive
            tabIndex={3}
            className="mt-3.5"
          >
            <CreateCharacterBtnRandom onClick={randomizeAge} />
          </TextInput>
        </ReactFocusLock>
      </CreateCharacterScrollArea>
      <div className="pt-2 mt-auto">
        <div className="flex flex-col gap-1">
          {(!personalData.firstName || !personalData.lastName || !isNumber(personalData.age)) && (
            <div className="alert alert--danger">*Заполните все обязательные поля</div>
          )}
          {!!(personalData.firstName && personalDataSeparatedErrors.firstName) && (
            <div className="alert alert--danger">{`Имя - ${personalDataSeparatedErrors.firstName}`}</div>
          )}
          {!!(personalData.lastName && personalDataSeparatedErrors.lastName) && (
            <div className="alert alert--danger">{`Фамилия - ${personalDataSeparatedErrors.lastName}`}</div>
          )}
          {!!(isNumber(personalData.age) && personalDataSeparatedErrors.age) && (
            <div className="alert alert--danger">{`Возраст - ${personalDataSeparatedErrors.age}`}</div>
          )}
        </div>
        <Button onClick={continueHandler} className="w-full mt-1.5">
          Далее
        </Button>
      </div>
    </>
  );
}
